import { useAppSelector } from '@/stores/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '@/theme/colors';
import { KeywordItemType } from '@/api/type/commonTypes';
import { useRouterQuery } from '@/hook/router-query/useRouterQuery';
import { useLoading } from '@/context/LoadingContenxt';
import CommonTitle from '@/components/title/CommonTitle';
import CommonSearch from '@/components/search/CommonSearch';
import Pagination from '@/components/pagination/Pagination';
import { partner } from '@/stores/slices/partner-list/partnerSlice';
import { getPlannerList } from '@/api/partner-list/planner/getPlannerList';
import PlannerListItem from './list/PlannerListItem';
import { useTranslation } from 'react-i18next';

const keywordTypeData: KeywordItemType[] = [
  {
    keyword: 'LOGIN_ID',
    i18nKey: 'common.loginId',
  },
  {
    keyword: 'PLANNER_NAME',
    i18nKey: 'plannerList.plannerName',
  },
  {
    keyword: 'NAME',
    i18nKey: 'common.subscriberName',
  },
  {
    keyword: 'REGISTERED_AT',
    i18nKey: 'common.registeredDate',
  },
];

const SIZE = 30;
const PlannerListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? 1);
  const size = Number(searchParams.get('size') ?? SIZE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordItemType>(keywordTypeData[0]);
  const dispatch = useDispatch();
  const keywordType = searchParams.get('keywordType');
  const { plannerList } = useAppSelector((state) => state.partner);
  const { startLoading, stopLoading } = useLoading();
  const { updateQuery } = useRouterQuery();
  const searchValueRef = useRef('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  /** 목록 fetch 함수 */
  const fetchPlannerList = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlannerList({
        keywordType: keywordType ?? 'LOGIN_ID',
        keyword: searchValueRef.current,
        page: currentPage,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(partner.setPlannerList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, keywordType, startLoading, stopLoading]);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setSearchParams({
      page: String(page),
      size: String(size),
    });
  };

  /** 검색 value 변경 */
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      searchValueRef.current = e.target.value;
    },
    []
  );

  /** 검색 아이콘 클릭 및 엔터 */
  const onClickSearch = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlannerList({
        keywordType: selectedKeywordType.keyword,
        keyword: searchValueRef.current,
        page: 1,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(partner.setPlannerList(res.data));
        updateQuery({
          params: {
            keywordType: selectedKeywordType.keyword,
            keyword: searchValueRef.current,
            page: 1,
            size: SIZE,
          },
          route: '/partner/planner-list',
        });
      } else {
        if (res.error.code === 'E_DATE_001') {
          alert(t('common.searchPlaceholder'));
        }
      }
    } finally {
      stopLoading();
    }
  }, [
    dispatch,
    selectedKeywordType.keyword,
    startLoading,
    stopLoading,
    t,
    updateQuery,
  ]);

  /** 검색 초기화 */
  const onClickSearchReset = useCallback(() => {
    setSearchParams({
      page: String(1),
      size: String(size),
    });
    setSearchValue('');
    searchValueRef.current = '';
    fetchPlannerList();
    navigate('/partner/planner-list');
  }, [fetchPlannerList, navigate, setSearchParams, size]);

  /** keywordType 저장 */
  const onDispatchKeyword = useCallback((keywordType: KeywordItemType) => {
    setSelectedKeywordType(keywordType);
  }, []);

  useEffect(() => {
    fetchPlannerList();
  }, [fetchPlannerList]);

  if (!plannerList) return;

  return (
    <Container>
      <CommonTitle
        title={t('plannerList.title')}
        count={plannerList.totalElements}
      />
      <SearchWrapper>
        <CommonSearch
          value={searchValue}
          onChange={onChangeSearch}
          onClickSearch={onClickSearch}
          onClickReset={onClickSearchReset}
          options={keywordTypeData}
          onSelect={onDispatchKeyword}
          selectedOption={selectedKeywordType}
        />
      </SearchWrapper>
      <ListWrapper>
        <PlannerListItem items={plannerList.items} />
      </ListWrapper>
      <Pagination
        totalItems={plannerList.totalElements}
        itemsPerPage={SIZE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default PlannerListPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SearchWrapper = styled.div`
  padding: 20px;
  background: ${colors.white};
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ListWrapper = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;
